import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Plus, Trash2, Paperclip, ExternalLink, FileText, Image } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || '';

const CATEGORIES = [
  { value: 'email_sent',        label: '📤 Email envoyé',           color: '#3B82F6' },
  { value: 'email_received',    label: '📥 Email reçu',             color: '#0D9F57' },
  { value: 'airline_response',  label: '✈️ Réponse compagnie',      color: '#8B5CF6' },
  { value: 'call',              label: '📞 Appel téléphonique',     color: '#E8920A' },
  { value: 'document',          label: '📄 Document',               color: '#6B7280' },
  { value: 'payment',           label: '💰 Paiement',               color: '#0D9F57' },
  { value: 'other',             label: '📝 Autre',                  color: '#9CA3AF' },
];

function getCat(value) {
  return CATEGORIES.find(c => c.value === value) || CATEGORIES[CATEGORIES.length - 1];
}

function formatDate(ts) {
  return new Date(ts).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

function FilePreview({ note }) {
  if (!note.file_name) return null;
  const isImage = note.file_mime?.startsWith('image/');
  const isPdf = note.file_mime === 'application/pdf';
  const href = `${API}/api/uploads/notes/${note.file_path}`;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={styles.fileLink}
    >
      {isImage ? <Image size={14} /> : isPdf ? <FileText size={14} /> : <Paperclip size={14} />}
      <span>{note.file_name}</span>
      <ExternalLink size={12} style={{ opacity: 0.6 }} />
    </a>
  );
}

export default function CaseJournal({ caseId }) {
  const [notes, setNotes] = useState([]);
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('other');
  const [file, setFile] = useState(null);
  const [adding, setAdding] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const fileRef = useRef();
  const bottomRef = useRef();

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/cases/${caseId}/notes`);
      setNotes(r.data);
    } catch (err) {
      console.error('Failed to load notes:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [caseId]);
  useEffect(() => {
    if (notes.length > 0) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [notes]);

  const handleAdd = async () => {
    if (!content.trim()) return;
    setAdding(true);
    try {
      const formData = new FormData();
      formData.append('content', content.trim());
      formData.append('category', category);
      if (file) formData.append('file', file);

      await axios.post(`${API}/api/cases/${caseId}/notes`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setContent('');
      setCategory('other');
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      setShowForm(false);
      await load();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.error || err.message));
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('Supprimer cette note ?')) return;
    try {
      await axios.delete(`${API}/api/cases/${caseId}/notes/${noteId}`);
      setNotes(prev => prev.filter(n => n.id !== noteId));
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };

  return (
    <div style={styles.wrap}>
      <div style={styles.header}>
        <h3 style={styles.title}>Journal du dossier</h3>
        <button onClick={() => setShowForm(f => !f)} style={styles.addBtn}>
          <Plus size={14} /> Ajouter une note
        </button>
      </div>

      {/* ADD FORM */}
      {showForm && (
        <div style={styles.form}>
          <select
            value={category}
            onChange={e => setCategory(e.target.value)}
            style={{ ...styles.select, borderColor: getCat(category).color }}
          >
            {CATEGORIES.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>

          <textarea
            value={content}
            onChange={e => setContent(e.target.value)}
            placeholder="Décrivez l'action ou l'événement..."
            style={styles.textarea}
            autoFocus
            onKeyDown={e => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleAdd();
            }}
          />

          <div style={styles.formBottom}>
            <label style={styles.fileLabel}>
              <Paperclip size={14} />
              {file ? file.name : 'Joindre un fichier (PDF, image, doc)'}
              <input
                type="file"
                ref={fileRef}
                style={{ display: 'none' }}
                accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.txt"
                onChange={e => setFile(e.target.files[0] || null)}
              />
            </label>
            <div style={styles.formActions}>
              <button onClick={() => { setShowForm(false); setContent(''); setFile(null); }} style={styles.cancelBtn}>
                Annuler
              </button>
              <button onClick={handleAdd} disabled={adding || !content.trim()} style={styles.submitBtn}>
                {adding ? 'Ajout...' : '✓ Ajouter'}
              </button>
            </div>
          </div>
          <p style={styles.hint}>Astuce : Cmd+Entrée (Mac) ou Ctrl+Entrée pour ajouter rapidement</p>
        </div>
      )}

      {/* TIMELINE */}
      <div style={styles.timeline}>
        {loading ? (
          <p style={styles.empty}>Chargement...</p>
        ) : notes.length === 0 ? (
          <p style={styles.empty}>Aucune note pour l'instant. Commencez par documenter les actions sur ce dossier.</p>
        ) : (
          notes.map((note, i) => {
            const cat = getCat(note.category);
            return (
              <div key={note.id} style={styles.noteItem}>
                {/* Timeline line */}
                <div style={styles.timelineLeft}>
                  <div style={{ ...styles.dot, background: cat.color }} />
                  {i < notes.length - 1 && <div style={styles.line} />}
                </div>

                <div style={styles.noteContent}>
                  <div style={styles.noteMeta}>
                    <span style={{ ...styles.catBadge, background: `${cat.color}18`, color: cat.color }}>
                      {cat.label}
                    </span>
                    <span style={styles.noteDate}>{formatDate(note.created_at)}</span>
                    <span style={styles.noteAuthor}>· {note.created_by}</span>
                  </div>

                  <p style={styles.noteText}>{note.content}</p>

                  <FilePreview note={note} />

                  <button
                    onClick={() => handleDelete(note.id)}
                    style={styles.deleteBtn}
                    title="Supprimer cette note"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}

const styles = {
  wrap: {
    background: '#fff',
    borderRadius: 14,
    padding: 20,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    gridColumn: '1 / -1', // spans full width in the 2-col grid
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 16,
  },
  title: {
    fontSize: 13, fontWeight: 800, color: '#0D1B3E',
    margin: 0, textTransform: 'uppercase', letterSpacing: 0.5,
  },
  addBtn: {
    display: 'flex', alignItems: 'center', gap: 6,
    background: '#0D1B3E', color: '#fff', border: 'none',
    borderRadius: 8, padding: '8px 14px', fontSize: 13,
    fontWeight: 700, cursor: 'pointer',
  },
  form: {
    background: '#F7F7F8', borderRadius: 10, padding: 16,
    marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 10,
  },
  select: {
    padding: '8px 12px', borderRadius: 8, border: '2px solid #ddd',
    fontSize: 13, fontWeight: 600, background: '#fff', outline: 'none',
    fontFamily: 'inherit',
  },
  textarea: {
    padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd',
    fontSize: 14, fontFamily: 'inherit', resize: 'vertical',
    minHeight: 80, outline: 'none', boxSizing: 'border-box', width: '100%',
  },
  formBottom: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    flexWrap: 'wrap', gap: 10,
  },
  fileLabel: {
    display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
    color: '#666', cursor: 'pointer', padding: '6px 10px',
    borderRadius: 6, border: '1px dashed #ccc', maxWidth: 260,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  formActions: { display: 'flex', gap: 8 },
  cancelBtn: {
    padding: '8px 14px', borderRadius: 8, border: '1px solid #ddd',
    background: '#fff', fontSize: 13, cursor: 'pointer', color: '#666',
  },
  submitBtn: {
    padding: '8px 16px', borderRadius: 8, border: 'none',
    background: '#E8920A', color: '#fff', fontSize: 13,
    fontWeight: 700, cursor: 'pointer',
  },
  hint: { fontSize: 11, color: '#aaa', margin: 0 },
  timeline: {
    display: 'flex', flexDirection: 'column', gap: 0,
    maxHeight: 500, overflowY: 'auto', paddingRight: 4,
  },
  noteItem: {
    display: 'flex', gap: 12, paddingBottom: 16,
  },
  timelineLeft: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    width: 16, flexShrink: 0,
  },
  dot: {
    width: 12, height: 12, borderRadius: '50%',
    flexShrink: 0, marginTop: 4, border: '2px solid #fff',
    boxShadow: '0 0 0 2px rgba(0,0,0,0.1)',
  },
  line: {
    width: 2, flex: 1, background: '#E5E7EB', marginTop: 4, minHeight: 20,
  },
  noteContent: {
    flex: 1, position: 'relative', paddingBottom: 4,
  },
  noteMeta: {
    display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap',
  },
  catBadge: {
    fontSize: 11.5, fontWeight: 700, padding: '3px 8px', borderRadius: 100,
  },
  noteDate: { fontSize: 12, color: '#888' },
  noteAuthor: { fontSize: 12, color: '#aaa' },
  noteText: {
    fontSize: 14, color: '#333', margin: '0 0 6px',
    lineHeight: 1.5, whiteSpace: 'pre-wrap',
  },
  fileLink: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    fontSize: 12.5, color: '#3B82F6', textDecoration: 'none',
    background: '#EFF6FF', padding: '4px 10px', borderRadius: 6,
    marginBottom: 4,
  },
  deleteBtn: {
    position: 'absolute', top: 0, right: 0,
    background: 'none', border: 'none', color: '#ccc',
    cursor: 'pointer', padding: 4, borderRadius: 6,
    display: 'flex',
  },
  empty: { fontSize: 13, color: '#aaa', textAlign: 'center', padding: '20px 0' },
};
